/**
 * Module-scoped toast dismiss generations.
 *
 * Instance refs reset on React StrictMode remount, so a sonner `onDismiss`
 * scheduled from the previous instance can match the new instance's generation
 * and clear the session. Keeping the counter outside the component avoids that.
 *
 * Unmount cleanup bumps the generation and defers `dismiss`: if a remount
 * claims again before the microtask runs, we skip dismiss so the remounted
 * loading toast is not removed by the previous instance's exit.
 */

const generations = new Map<string, number>();

export function claimToastGeneration(toastId: string): number {
  const next = (generations.get(toastId) ?? 0) + 1;
  generations.set(toastId, next);
  return next;
}

export function currentToastGeneration(toastId: string): number {
  return generations.get(toastId) ?? 0;
}

/** Drop the entry when this claim is still the latest (terminal / real unmount). */
export function releaseToastGeneration(
  toastId: string,
  expected: number,
): void {
  if (generations.get(toastId) === expected) {
    generations.delete(toastId);
  }
}

/** Test helper — clear between cases. */
export function resetToastGenerationsForTests(): void {
  generations.clear();
}
